import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { publicCmsAPI } from '../../lib/cmsApi';
import {
  Droplets,
  Building2,
  Route,
  Trees,
  FileText,
  FolderKanban,
  ArrowRight,
  Phone,
  CheckCircle,
  Quote,
  Target,
  Lightbulb,
  Users,
  Loader2,
} from 'lucide-react';

// Icon mapping for services
const iconMap = {
  Droplets,
  Building2,
  Route,
  Trees,
  FileText,
  FolderKanban,
  Target,
  Lightbulb,
  Users,
};

const HomePage = () => {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [partners, setPartners] = useState([]);
  const [pageContent, setPageContent] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, servicesRes, projectsRes, partnersRes, pageRes] = await Promise.all([
          publicCmsAPI.getSettings(),
          publicCmsAPI.getServices(true), // Featured only
          publicCmsAPI.getProjects(true), // Featured only
          publicCmsAPI.getPartners(),
          publicCmsAPI.getPage('home').catch(() => ({ data: null })),
        ]);
        
        setSettings(settingsRes.data);
        setServices(servicesRes.data || []);
        setProjects(projectsRes.data || []);
        setPartners(partnersRes.data || []);
        setPageContent(pageRes.data);
      } catch (error) {
        console.error('Error fetching CMS data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Get section content by key
  const getSection = (key) => {
    if (!pageContent?.sections) return null;
    return pageContent.sections.find(s => s.section_key === key);
  };

  const heroSection = getSection('hero');
  const aboutSection = getSection('about_preview');
  const valuesSection = getSection('values');
  const quoteSection = getSection('engagement_quote');
  const ctaSection = getSection('bottom_cta');
  const statsSection = getSection('stats');

  // Default values
  const stats = statsSection?.items || [
    { value: 'Depuis 2020', label: 'Au service des collectivités' },
    { value: '6', label: "Domaines d'expertise" },
    { value: 'Québec', label: 'Et Amérique du Nord' },
    { value: '100%', label: 'Engagement durable' },
  ];

  const values = valuesSection?.items || [
    {
      icon: 'Target',
      title: 'Durabilité',
      description: "Principes de gestion durable de l'eau et d'adaptation climatique intégrés à chaque mandat.",
    },
    {
      icon: 'Lightbulb',
      title: 'Excellence technique',
      description: "Expertise approfondie en hydraulique urbaine, modélisation et conception d'infrastructures.",
    },
    {
      icon: 'Users',
      title: 'Collaboration',
      description: "Approche collaborative favorisant l'échange entre experts, partenaires et clients.",
    },
  ];

  const aboutItems = aboutSection?.items || [
    { text: 'Vision globale du territoire' },
    { text: 'Maîtrise des cadres réglementaires' },
    { text: 'Culture de précision et de responsabilité' },
    { text: "Recherche constante d'optimisation et d'innovation" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#F9B438]" />
      </div>
    );
  }

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section
        className="relative min-h-[600px] lg:min-h-[700px] flex items-center bg-cover bg-center"
        style={{
          backgroundImage: `url('${heroSection?.background_image_url || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=2000&q=80'}')`,
        }}
      >
        <div className="absolute inset-0 bg-[#1D1D1B]/85" />
        
        <div className="container-app py-20 relative z-10">
          <div className="max-w-3xl animate-fadeIn">
            <div 
              className="inline-block px-4 py-1.5 rounded-full text-sm font-semibold mb-6 text-[#1D1D1B]"
              style={{ background: 'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)' }}
            >
              {heroSection?.eyebrow || settings?.tagline || "Experts en hydrologie urbaine et en conception d'infrastructures durables"}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              {heroSection?.title || 'Concevoir des villes'}
              <span 
                className="block mt-2"
                style={{ 
                  background: 'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {heroSection?.subtitle || 'résilientes, performantes et durables'}
              </span>
            </h1>
            {heroSection?.content?.split('\n\n').map((paragraph, idx) => (
              <p key={idx} className={`${idx === 0 ? 'text-lg sm:text-xl text-white/70 mb-4' : 'text-base text-white/60 mb-8'} leading-relaxed`}>
                {paragraph}
              </p>
            )) || (
              <>
                <p className="text-lg sm:text-xl text-white/70 mb-4 leading-relaxed">
                  PEY Consultants Inc. est une firme d'ingénierie spécialisée en génie civil, en hydrologie urbaine, en modélisation hydraulique et en gestion durable des eaux pluviales.
                </p>
                <p className="text-base text-white/60 mb-8 leading-relaxed">
                  Grâce à une approche fondée sur la modélisation avancée, l'analyse des bassins versants et l'optimisation des réseaux municipaux, nous développons des solutions adaptées aux réalités climatiques actuelles et aux exigences réglementaires en vigueur.
                </p>
              </>
            )}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to={heroSection?.cta_link || '/contact'}>
                <Button size="lg" className="w-full sm:w-auto" data-testid="hero-contact-btn">
                  {heroSection?.cta_text || settings?.primary_cta_text || 'Demander une soumission'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to={heroSection?.cta_secondary_link || '/services'}>
                <Button 
                  size="lg" 
                  variant="outline-light" 
                  className="w-full sm:w-auto"
                >
                  {heroSection?.cta_secondary_text || settings?.secondary_cta_text || 'Notre expertise'}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section 
        className="py-12"
        style={{ background: 'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)' }}
      >
        <div className="container-app">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="font-heading text-2xl sm:text-3xl font-bold text-[#1D1D1B] mb-2">
                  {stat.value}
                </p>
                <p className="text-[#1D1D1B]/70 text-sm sm:text-base">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview - Qui nous sommes */}
      <section className="section-padding bg-white">
        <div className="container-app">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fadeIn">
              <p className="text-[#F9B438] font-semibold mb-2">{aboutSection?.eyebrow || 'QUI NOUS SOMMES'}</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1D1D1B] mb-6 section-header">
                {aboutSection?.title || 'Une expertise structurée au service de décisions éclairées'}
              </h2>
              {aboutSection?.content?.split('\n\n').map((paragraph, idx) => (
                <p key={idx} className="text-neutral-600 mb-6 leading-relaxed mt-8">
                  {paragraph}
                </p>
              )) || (
                <>
                  <p className="text-neutral-600 mb-6 leading-relaxed mt-8">
                    PEY Consultants Inc. est une firme québécoise de génie-conseil qui accompagne les organisations publiques et privées dans la réalisation de projets structurants.
                  </p>
                  <p className="text-neutral-600 mb-6 leading-relaxed">
                    Notre approche repose sur l'analyse rigoureuse des données, une méthodologie éprouvée et une capacité à traduire la complexité technique en solutions concrètes.
                  </p>
                </>
              )}
              <ul className="space-y-3 mb-8">
                {aboutItems.map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div 
                      className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, rgba(255,213,55,0.2) 0%, rgba(249,180,56,0.2) 100%)' }}
                    >
                      <CheckCircle className="w-4 h-4 text-[#F9B438]" />
                    </div>
                    <span className="text-neutral-700">{item.text}</span>
                  </li>
                ))}
              </ul>
              <Link to={aboutSection?.cta_link || '/a-propos'}>
                <Button variant="secondary">
                  {aboutSection?.cta_text || 'En savoir plus'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src={aboutSection?.image_url || 'https://images.unsplash.com/photo-1581092160607-ee22621dd758?auto=format&fit=crop&w=800&q=80'}
                alt="Ingénierie hydraulique"
                className="rounded-xl shadow-2xl w-full object-cover h-[450px]"
              />
              <div 
                className="absolute -bottom-6 -left-6 p-6 rounded-xl shadow-lg hidden md:block text-[#1D1D1B]"
                style={{ background: 'linear-gradient(135deg, #FFD537 0%, #F9B438 100%)' }}
              >
                <p className="font-heading text-3xl font-bold">{aboutSection?.custom_data?.foundation_year || '2020'}</p>
                <p className="text-sm font-medium">Année de fondation</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-neutral-50" data-testid="services-section">
        <div className="container-app">
          <div className="text-center mb-12">
            <p className="text-[#F9B438] font-semibold mb-2">EXPERTISE PLURIDISCIPLINAIRE</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1D1D1B] mb-4">
              Une expertise spécialisée au service des infrastructures urbaines
            </h2>
            <p className="text-neutral-600 max-w-3xl mx-auto mt-6">
              PEY Consultants met à profit une expertise technique pointue en ingénierie municipale.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Droplets;
              return (
                <Card
                  key={service.id}
                  className="group cursor-pointer animate-fadeIn"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <CardContent className="p-6">
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-all duration-300"
                      style={{ background: 'linear-gradient(135deg, rgba(255,213,55,0.15) 0%, rgba(249,180,56,0.15) 100%)' }}
                    >
                      <IconComponent className="w-7 h-7 text-[#F9B438]" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg mb-2 text-[#1D1D1B]">{service.title}</h3>
                    <p className="text-neutral-600 text-sm">{service.short_description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Link to="/services">
              <Button variant="outline">
                Voir tous nos services
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-white">
        <div className="container-app">
          <div className="text-center mb-12">
            <p className="text-[#F9B438] font-semibold mb-2">{valuesSection?.eyebrow || 'CE QUI NOUS GUIDE'}</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1D1D1B] mb-4">
              {valuesSection?.title || 'Nos valeurs'}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const IconComponent = iconMap[value.icon] || Target;
              return (
                <Card key={index} className="group animate-fadeIn" style={{ animationDelay: `${index * 100}ms` }}>
                  <CardContent className="p-8 text-center">
                    <div 
                      className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-6 transition-all duration-300 group-hover:scale-110"
                      style={{ background: 'linear-gradient(135deg, rgba(255,213,55,0.15) 0%, rgba(249,180,56,0.15) 100%)' }}
                    >
                      <IconComponent className="w-8 h-8 text-[#F9B438]" />
                    </div>
                    <h3 className="font-heading font-semibold text-xl mb-3 text-[#1D1D1B]">{value.title}</h3>
                    <p className="text-neutral-600">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Projects Preview */}
      {projects.length > 0 && (
        <section className="section-padding bg-neutral-50">
          <div className="container-app">
            <div className="text-center mb-12">
              <p className="text-[#F9B438] font-semibold mb-2">NOS PROJETS</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1D1D1B] mb-4">
                Notre expertise concrétisée
              </h2>
              <p className="text-neutral-600 max-w-2xl mx-auto">
                Découvrez nos réalisations à travers le Québec et l'Amérique du Nord.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {projects.slice(0, 2).map((project) => (
                <Card key={project.id} className="overflow-hidden group">
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={project.image_url || 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?auto=format&fit=crop&w=800&q=80'}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1D1D1B]/80 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4 text-white">
                      <p className="text-sm text-[#FFD537] font-medium">{project.location}</p>
                      <h3 className="font-heading font-semibold text-lg">{project.title}</h3>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            <div className="text-center mt-10">
              <Link to="/projets">
                <Button variant="outline">
                  Voir tous nos projets
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Quote Section */}
      <section 
        className="py-16"
        style={{ background: 'linear-gradient(135deg, rgba(255,213,55,0.1) 0%, rgba(249,180,56,0.1) 100%)' }}
      >
        <div className="container-app">
          <div className="max-w-3xl mx-auto text-center">
            <Quote className="w-12 h-12 text-[#FFD537] mx-auto mb-6" />
            <blockquote className="text-xl sm:text-2xl text-[#1D1D1B] font-medium italic mb-6 leading-relaxed">
              « {quoteSection?.content || "Chez PEY Consultants, nous sommes convaincus qu'un environnement urbain résilient repose sur une gestion responsable, durable et innovante de l'eau."} »
            </blockquote>
            <p className="text-[#F9B438] font-semibold">{quoteSection?.subtitle || '— NOTRE ENGAGEMENT'}</p>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      {partners.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container-app">
            <div className="text-center mb-10">
              <p className="text-[#F9B438] font-semibold mb-2">NOS PARTENAIRES</p>
              <h2 className="font-heading text-2xl font-bold text-[#1D1D1B]">
                Nos collaborateurs dans le paysage de l'ingénierie
              </h2>
            </div>
            <div className="flex flex-wrap justify-center gap-8 items-center">
              {partners.map((partner) => (
                <div 
                  key={partner.id}
                  className="px-6 py-3 bg-neutral-50 rounded-lg text-neutral-600 font-medium"
                >
                  {partner.name}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="bg-[#1D1D1B] py-16">
        <div className="container-app text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4">
            {ctaSection?.title || 'Prêt à démarrer votre projet?'}
          </h2>
          <p className="text-white/60 mb-8 max-w-xl mx-auto">
            {ctaSection?.content || 'Contactez notre équipe pour discuter de vos besoins en gestion des eaux et infrastructures durables.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={ctaSection?.cta_link || '/contact'}>
              <Button size="lg" className="w-full sm:w-auto">
                {ctaSection?.cta_text || 'Nous contacter'}
              </Button>
            </Link>
            <a href={ctaSection?.cta_secondary_link || 'tel:+15145551234'}>
              <Button 
                size="lg" 
                variant="outline-light" 
                className="w-full sm:w-auto"
              >
                <Phone className="w-4 h-4 mr-2" />
                {ctaSection?.cta_secondary_text || 'Appelez-nous'}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Client Portal Banner */}
      <section className="bg-neutral-100 py-12">
        <div className="container-app">
          <Card className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="font-heading text-xl font-semibold text-[#1D1D1B] mb-2">
                  Vous êtes déjà client?
                </h3>
                <p className="text-neutral-600">
                  Accédez à votre espace sécurisé pour consulter vos projets et documents.
                </p>
              </div>
              <Link to="/connexion">
                <Button className="whitespace-nowrap" data-testid="portal-access-btn">
                  Accéder à l'espace client
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
